import React from 'react';
import { Link } from 'react-router-dom';
import type { components } from '../../types/learnerApi';
import { useImageUrl } from '../../services/uploads';
import { getEnrolmentAvailability } from '../../utils/enrolmentAvailability';
import ProgressBar from './ProgressBar';

type CourseCardData = components['schemas']['CourseCard'];

interface CourseCardProps {
  course: CourseCardData;
}

type DisplayState = 'not_started' | 'in_progress' | 'finalized';

// "Not Started" absorbs the old Upcoming state — untouched open-window cards
// and not-yet-open cards share it; the date caption ("Started Aug 31" vs
// "Starts Sep 5") carries the distinction (Aleix, 8/31).
const bucketLabel: Record<DisplayState, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  finalized: 'Completed', // expired cards override to 'Expired' at render
};

// Finalized covers two underlying states (completed normally, or expired before
// completion). The chip shares a single neutral palette — the progress bar
// inside the card already conveys whether the learner actually finished.
const bucketColor: Record<DisplayState, string> = {
  not_started: 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/40',
  in_progress: 'text-secondary-600 dark:text-secondary-400 bg-secondary-50 dark:bg-secondary-950/40',
  finalized: 'text-text-secondary bg-text-tertiary/10',
};

// Short, locale-aware date for the start-date caption — e.g. "Jan 12".
// Returns null for missing/invalid dates so the caller can hide the line.
function formatStartDate(value: string | null | undefined): { label: string; tense: 'past' | 'future' } | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const tense: 'past' | 'future' = date.getTime() > Date.now() ? 'future' : 'past';
  const label = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return { label, tense };
}

// Deadline caption — learners keep seeing the end date while the window is
// open (Aleix, 8/31). End dates are date-only and inclusive, so "days left"
// counts to the end of that day.
function formatEndDate(value: string | null | undefined): { label: string; daysLeft: number } | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  const daysLeft = Math.floor((endOfDay.getTime() - Date.now()) / 86_400_000);
  const label = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return { label, daysLeft };
}

const CourseCard: React.FC<CourseCardProps> = ({ course }) => {
  // Navigate by `assignment_id` (the enrolment) so two cycles of the same
  // course land on different routes. Fall back to `course_id` for stale
  // responses cached before the rename.
  const href = `/learn/courses/${course.assignment_id ?? course.course_id}`;
  // Prefer the nested `progress` block (added in the latest backend cut) and
  // fall back to the flat fields for cached responses + older clients.
  const nested = (course as unknown as { progress?: {
    modules_completed?: number;
    modules_total?: number;
    progress_percent?: number;
    bucket?: 'in_progress' | 'upcoming' | 'completed';
  } }).progress;
  const bucket = nested?.bucket ?? course.bucket ?? 'in_progress';
  const progress = nested?.progress_percent ?? course.progress_percent ?? 0;
  const modulesCompleted = nested?.modules_completed ?? course.modules_completed ?? 0;
  const modulesTotal = nested?.modules_total ?? course.modules_total ?? 0;
  const [coverUrl] = useImageUrl(course.image_url ?? null);
  // Start-date caption — distinguishes two enrolments of the same course.
  const startDate = formatStartDate(course.start_date);
  const endDate = formatEndDate(course.end_date);
  // Date-window gate. The card stays clickable when expired — the gate UI
  // lives on the detail page so the learner can see why. Both completed and
  // expired collapse into a single "Finalized" display state; the cover dim
  // signals the sub-distinction (ran out of time vs. finished cleanly).
  const ranOutOfTime =
    getEnrolmentAvailability(
      { start_date: course.start_date, end_date: course.end_date },
      { completed: false },
    ).kind === 'expired' && bucket !== 'completed';
  const displayState: DisplayState =
    bucket === 'completed' || ranOutOfTime
      ? 'finalized'
      : bucket === 'upcoming' ||
          (bucket === 'in_progress' && progress === 0 && modulesCompleted === 0)
        ? 'not_started' // untouched — window not open yet, or open but unvisited
        : 'in_progress';

  return (
    <Link
      to={href}
      aria-label={ranOutOfTime ? `${course.title} — finalized, expired` : course.title ?? undefined}
      className="group flex flex-col rounded-[28px] border-[7px] border-[rgba(189,188,201,0.24)] bg-surface-primary shadow-[7px_7px_14px_0px_rgba(0,0,0,0.2)] transition-shadow overflow-hidden"
    >
      {coverUrl ? (
        <img
          src={coverUrl}
          alt={course.title}
          className={`w-full h-36 object-cover transition-opacity ${
            ranOutOfTime ? 'opacity-60' : ''
          }`}
        />
      ) : (
        <div
          className={`w-full h-36 bg-gradient-to-br from-primary-200 to-primary-400 dark:from-primary-800 dark:to-primary-600 flex items-center justify-center transition-opacity ${
            ranOutOfTime ? 'opacity-60' : ''
          }`}
        >
          <span className="text-3xl font-bold text-white/70">{course.title?.[0] ?? '?'}</span>
        </div>
      )}

      <div className="flex flex-col gap-2 p-4 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-text-primary line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
            {course.title}
          </h3>
          <span className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${bucketColor[displayState]}`}>
            {ranOutOfTime ? 'Expired' : bucketLabel[displayState]}
          </span>
        </div>

        {course.subtitle && (
          <p className="text-xs text-text-secondary line-clamp-1">{course.subtitle}</p>
        )}

        {(startDate || (endDate && displayState !== 'finalized')) && (
          <p className="text-xs text-text-tertiary">
            {startDate && `${startDate.tense === 'future' ? 'Starts' : 'Started'} ${startDate.label}`}
            {startDate && endDate && displayState !== 'finalized' && ' · '}
            {endDate && displayState !== 'finalized' && (
              <span
                className={
                  endDate.daysLeft <= 7
                    ? 'font-semibold text-secondary-600 dark:text-secondary-400'
                    : undefined
                }
              >
                {endDate.daysLeft <= 0
                  ? 'Ends today'
                  : endDate.daysLeft === 1
                    ? 'Ends tomorrow'
                    : endDate.daysLeft <= 7
                      ? `Ends in ${endDate.daysLeft} days`
                      : `Ends ${endDate.label}`}
              </span>
            )}
          </p>
        )}

        <div className="mt-auto pt-2 space-y-1.5">
          <ProgressBar percent={progress} />
          <div className="flex items-center justify-between text-xs text-text-secondary">
            <span>{modulesCompleted}/{modulesTotal} modules</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>

        {course.estimated_duration_minutes && (
          <p className="text-xs text-text-tertiary">
            ~{course.estimated_duration_minutes} min
          </p>
        )}
      </div>
    </Link>
  );
};

export default CourseCard;
